import {
  PORTAL_REQUEST_VIEW_LABELS,
  PORTAL_REQUEST_VIEWS,
  PORTAL_WEB_PATHS,
  type PortalRequestSummary,
  type PortalRequestView,
} from '@fos/shared/contracts/portal';
import { Flag, Inbox, MessageSquare, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { formatRelativePast, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { TabPanel, Tabs } from '../../../ui/Tabs';
import { NewRequestDialog } from '../components/NewRequestDialog';
import { PortalStatusPill } from '../components/PortalStatus';

/**
 * Portal klienta — lista zgłoszeń (DECISIONS 2026-10-17 [P] „Model portalu klienta” nr 4): zakładki „Otwarte” /
 * „Zamknięte” z licznikami (`?widok=zamkniete`), wiersz = numer, tytuł, status po ludzku, ostatnia zmiana i liczba
 * wiadomości; cały wiersz prowadzi do szczegółów. „Nowe zgłoszenie” w nagłówku (desktop) i przypięte na dole ekranu
 * (telefon, pod kciukiem). Najpierw telefon: wiersze w jednej kolumnie, bez przewijania w bok.
 */
export function PortalHomePage() {
  const [params, setParams] = useSearchParams();
  const view: PortalRequestView = params.get('widok') === 'zamkniete' ? 'closed' : 'open';
  const [newOpen, setNewOpen] = useState(false);
  const list = trpc.portal.listRequests.useQuery({ view });
  const counts = list.data?.counts;

  const setView = (next: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'closed') p.set('widok', 'zamkniete');
        else p.delete('widok');
        return p;
      },
      { replace: true },
    );

  return (
    <Page width="reading" className="pb-32 md:pb-16">
      <header className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between md:gap-4">
        <div className="min-w-0">
          <h1 className="text-h2 text-fg md:text-h3">Twoje zgłoszenia</h1>
          <p className="mt-1 text-body-s text-fg-secondary">
            Zgłoś, czego potrzebujesz. Status i odpowiedzi zobaczysz tutaj, a o odpowiedzi damy znać mailem.
          </p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => setNewOpen(true)}
          className="hidden shrink-0 md:inline-flex"
        >
          Nowe zgłoszenie
        </Button>
      </header>

      <Tabs
        value={view}
        onValueChange={setView}
        aria-label="Zgłoszenia"
        className="mt-5"
        items={PORTAL_REQUEST_VIEWS.map((v) => ({
          value: v,
          label: PORTAL_REQUEST_VIEW_LABELS[v],
          ...(counts ? { count: counts[v] } : {}),
        }))}
      >
        {PORTAL_REQUEST_VIEWS.map((v) => (
          <TabPanel key={v} value={v} className="pt-4">
            {v === view && (
              <Section padding="none" className="overflow-hidden">
                {list.isPending ? (
                  <div className="p-4 md:px-6">
                    <ListSkeleton rows={3} />
                  </div>
                ) : list.isError ? (
                  <ErrorState onRetry={() => void list.refetch()} retrying={list.isFetching} />
                ) : list.data.items.length === 0 ? (
                  <EmptyList view={v} onNew={() => setNewOpen(true)} />
                ) : (
                  <ul aria-label={`Zgłoszenia: ${PORTAL_REQUEST_VIEW_LABELS[v].toLowerCase()}`}>
                    {list.data.items.map((item) => (
                      <RequestRow key={item.id} item={item} />
                    ))}
                  </ul>
                )}
              </Section>
            )}
            {v === view && list.data?.truncated && (
              <p className="mt-3 text-caption text-fg-muted">Widać najnowsze {list.data.items.length} zgłoszeń.</p>
            )}
          </TabPanel>
        ))}
      </Tabs>

      {/* telefon: główna akcja pod kciukiem (portal nie ma dolnego menu zespołu) */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-canvas px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] md:hidden">
        <Button variant="primary" size="l" icon={Plus} fullWidth onClick={() => setNewOpen(true)} className="h-12">
          Nowe zgłoszenie
        </Button>
      </div>

      <NewRequestDialog open={newOpen} onOpenChange={setNewOpen} />
    </Page>
  );
}

function EmptyList({ view, onNew }: { view: PortalRequestView; onNew: () => void }) {
  if (view === 'closed') {
    return (
      <EmptyState
        icon={Inbox}
        title="Nie ma jeszcze zamkniętych zgłoszeń"
        description="Tutaj trafią zgłoszenia zakończone i anulowane."
      />
    );
  }
  return (
    <EmptyState
      icon={Inbox}
      title="Nie masz otwartych zgłoszeń"
      description="Gdy czegoś potrzebujesz — zmiany na stronie, poprawki błędu albo odpowiedzi na pytanie — dodaj zgłoszenie."
      action={
        <Button variant="secondary" icon={Plus} onClick={onNew}>
          Nowe zgłoszenie
        </Button>
      }
    />
  );
}

function RequestRow({ item }: { item: PortalRequestSummary }) {
  const messages = item.messageCount;
  return (
    <li className="relative border-b border-line transition-colors last:border-b-0 hover:bg-surface active:bg-surface">
      <div className="flex min-h-[72px] items-start gap-3 px-4 py-3.5 md:items-center md:px-6">
        <Mono className="mt-0.5 w-10 shrink-0 text-caption text-fg-muted md:mt-0">#{item.number}</Mono>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 md:flex-row md:items-center md:gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Link
              to={PORTAL_WEB_PATHS.request(item.id)}
              className="line-clamp-2 text-body-m font-medium break-words text-fg outline-none after:absolute after:inset-0 focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-accent md:text-body-s"
            >
              {item.title}
            </Link>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-fg-muted">
              {item.urgent && (
                <span className="inline-flex items-center gap-1 font-medium text-warning">
                  <Flag size={12} strokeWidth={2} aria-hidden fill="currentColor" fillOpacity={0.18} />
                  Pilne
                </span>
              )}
              <span>zmiana: {formatRelativePast(item.updatedAt)}</span>
              {messages > 0 && (
                <span className="inline-flex items-center gap-1">
                  <MessageSquare size={12} strokeWidth={1.75} aria-hidden />
                  {messages} {plural(messages, ['wiadomość', 'wiadomości', 'wiadomości'])}
                </span>
              )}
            </span>
          </div>
          <PortalStatusPill status={item.status} className="self-start md:self-center" />
        </div>
      </div>
    </li>
  );
}
